import { EvidenceType, InfrigmentOfUser, InfrigmentType, ReporterType, SanctionRecommendation,
   SanctionRecommendationByAsset, SanctionType, SeverityType } from '@/interfaces/infractions';
import { StoreState } from '@/store';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { exhaustMap, map } from 'rxjs/operators';
import { CacheService } from '@/utils/cacheService/cache.service';

@Injectable({
  providedIn: 'root',
})
export class InfractionService {

  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private store: Store<StoreState>,
  ) { }

  getSeverityTypes(assetId: number): Observable<SeverityType[]> {
    const source$ = this.http.get<SeverityType[]>(`/Infringement/InfringementSeverityTypes?assetId=${assetId}`);
    return this.cacheService.save<SeverityType[]>(`InfringementSeverityTypes${assetId}`, 24 * 60, source$);
  }

  getInfrigmentTypes(assetId: number, severityTypeId: number): Observable<InfrigmentType[]> {
    const source$ = this.http.get<InfrigmentType[]>(`/Infringement/InfringementTypes?assetId=${assetId}&severityId=${severityTypeId}`);
    return this.cacheService.save<InfrigmentType[]>(`InfringementTypes${assetId}_${severityTypeId}`, 24 * 60, source$);
  }

  getReportersTypes(assetId: number): Observable<ReporterType[]> {
    const source$ = this.http.get<ReporterType[]>(`/Infringement/InfringementReports?assetId=${assetId}`);
    return this.cacheService.save<ReporterType[]>(`InfringementReports${assetId}`, 24 * 60, source$);
  }

  getEvidenceTypes(assetId: number): Observable<EvidenceType[]> {
    const source$ = this.http.get<EvidenceType[]>(`/Infringement/EvidenceInfringements?assetId=${assetId}`);
    return this.cacheService.save<EvidenceType[]>(`EvidenceInfringements${assetId}`, 24 * 60, source$);
  }

  getSanctionTypes(assetId: number): Observable<SanctionType[]> {
    const source$ = this.http.get<SanctionType[]>(`/Infringement/SanctionTypes?assetId=${assetId}`);
    return this.cacheService.save<EvidenceType[]>(`SanctionTypes${assetId}`, 24 * 60, source$);
  }

  getRecomendation(assetId: number, severityTypeId: number, nroInfringements: number): Observable<SanctionRecommendation> {
    const source$ = this.http.get<SanctionRecommendation>(`/Infringement/RecomendationInfringement?assetId=${assetId}&severityTypeId=${severityTypeId}&nroInfringements=${nroInfringements}`);
    return this.cacheService.save<SanctionRecommendation>(`RecomendationInfringement${assetId}_${severityTypeId}_${nroInfringements}`, 24 * 60, source$);
  }

  getInfringementsByUser(mdmid: string): Observable<InfrigmentOfUser[]> {
    return this.http.get<InfrigmentOfUser[]>(`/Infringement/InfringementsByUser?userRutDv=${mdmid}`);
  }

  getInfringementsQuantityByUser(mdmid: string): Observable<number> {
    return this.http.get<InfrigmentOfUser[]>(`/Infringement/InfringementsByUser?userRutDv=${mdmid}`).pipe(
      map((infrigmentOfUsers) => infrigmentOfUsers.length),
    );
  }

  getSanctionRulesMatrix(assetId: number): Observable<string[][]> {
    // Diccionario de numeros ordinales
    const ordinals: Record<string, string> = {
      1: 'ra',
      2: 'da',
      3: 'ra',
      4: 'ta',
      5: 'ta',
      6: 'ta',
      7: 'ma',
      8: 'va',
      9: 'no',
      10: 'ma',
    };
    const source$ = this.http.get<SanctionRecommendationByAsset[]>(`/Infringement/SanctionRecommendationsByAsset?assetId=${assetId}`).pipe(
      map((infrigmentOfUsers) => {

        const columns = [
          // 2: limpio los duplicados transformando el arreglo en un set y luego lo vuelvo a transformar a un arreglo
          ...new Set(
            // 1: obtengo todos los nros
            infrigmentOfUsers.map(({ nroInfringement }) => nroInfringement)
          ),
        ]
          .sort((a, b) => a - b); // 3: ordeno de menor a mayor
        // .map((nro) => `${nro}${ordinals[nro]} infracción`); // 4: agrego nomenclatura ordinal e 'infraccion'
        const rows = [
          // 2: limpio los duplicados transformando el arreglo en un set y luego lo vuelvo a transformar a un arreglo
          ...new Set(
            // 1: obtengo todos las severidades
            infrigmentOfUsers.map(({ severity }) => severity),
          ),
        ]
          .sort((a, b) => a.length - b.length); // 3: ordeno de menor a mayor segun largo del campo
        // .map((nro) => `${nro}${ordinals[nro]} infracción`); // 4: agrego nomenclatura ordinal e 'infraccion'

        const matrix: Array<Array<string>> = [];

        // Obtengo los datos segun las rows y columns
        // y los almaceno en la matriz
        for (let index = 0; index < rows.length; index++) {
          const rowName = rows[index];
          const row = [];
          for (let indexColumn = 0; indexColumn < columns.length; indexColumn++) {
            const columnName = columns[indexColumn];
            const infrigment = infrigmentOfUsers.find(({ nroInfringement, severity }) => (
              nroInfringement === +columnName && severity === rowName
            ));
            if (infrigment) {
              row[indexColumn] = infrigment.description;
            }
          }
          matrix.push(row);
        }

        // agrego las filas a la matriz
        rows.map((row, index) => {
          matrix[index].unshift(row);
        });

        // agrego las columnas a la matriz
        matrix.unshift(['', ...columns.map((nro) => `${nro}${ordinals[nro]} infracción`)]);

        return matrix;
      }),
    );
    return this.cacheService.save<string[][]>(`sanctionRulesMatrix${assetId}`, 24 * 60, source$);
  }

  createInfringement(infrigment: any): Observable<{ id: number }> {
    this.cacheService.remove('LicensesByUser');
    return this.store.select('login').pipe(
      exhaustMap((user) =>
        this.http.post<{ id: number }>('/Infringement/Create', { ...infrigment, creatorMdmid: user.mdmid }),
      ),
    );
  }

  updateInfringement(id: number, infrigment: any): Observable<{ id: number }> {
    this.cacheService.remove('LicensesByUser');
    return this.store.select('login').pipe(
      exhaustMap((user) =>
        this.http.post<number>(`/Infringement/InfringementId/${id}/Update`, { ...infrigment, creatorMdmid: user.mdmid }).pipe(map((idReq) => ({ id: idReq }))),
      ),
    );
  }

}
